import React, { useMemo, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { X, Check, RotateCw, Trophy, AlertTriangle } from 'lucide-react';

// Fisher–Yates shuffle — returns a new array, doesn't mutate.
const shuffle = (arr) => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

/**
 * QuizPanel — supports MCQ, True/False, and short-answer questions with:
 *   • 80% passing gate (from quiz.passing_score, defaults to 80)
 *   • Unlimited retakes — questions shuffled each attempt
 *   • Retry-until-pass UX; the "Back to Lesson" CTA only appears once
 *     the student passes (so they can't bypass the gate).
 *
 * Note: state (attempt number, answers, result) is owned locally here so
 * the parent doesn't have to know about shuffle semantics. The parent
 * only supplies the quiz payload and the submit handler.
 */
export const QuizPanel = ({ quiz, onSubmit, onClose }) => {
  const [attempt, setAttempt] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fresh shuffled question order every attempt.
  const shuffled = useMemo(() => shuffle(quiz.questions || []), [attempt, quiz.questions]);

  const passingScore = quiz.passing_score || 80;

  const setAnswer = (idx, val) => setAnswers((a) => ({ ...a, [idx]: val }));

  const submit = useCallback(async () => {
    // Package answers in the ORIGINAL question order so the backend can grade.
    const originalOrder = (quiz.questions || []).map((orig) => {
      const shuffledIdx = shuffled.findIndex((sq) => sq === orig);
      return answers[shuffledIdx];
    });
    setSubmitting(true);
    try {
      const res = await onSubmit(originalOrder);
      setResult(res);
    } finally {
      setSubmitting(false);
    }
  }, [answers, shuffled, quiz.questions, onSubmit]);

  const retry = () => {
    setAnswers({});
    setResult(null);
    setAttempt((a) => a + 1);
  };

  const allAnswered = shuffled.every((_, i) => answers[i] !== undefined && answers[i] !== '');

  return (
    <div className="bg-brand-surface border border-white/5 rounded-xl overflow-hidden" data-testid="quiz-interface">
      <div className="p-4 border-b border-border flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{quiz.title}</h3>
          <p className="text-sm text-muted-foreground">
            Pass score: {passingScore}% · {shuffled.length} question{shuffled.length !== 1 ? 's' : ''}
            {attempt > 0 && ` · Attempt ${attempt + 1}`}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="quiz-close-btn">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {result ? (
        <div className="p-6 text-center">
          <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center ${
            result.passed ? 'bg-green-500/20' : 'bg-red-500/20'
          }`}>
            {result.passed
              ? <Trophy className="w-12 h-12 text-green-400" data-testid="quiz-pass-icon" />
              : <AlertTriangle className="w-12 h-12 text-red-400" data-testid="quiz-fail-icon" />}
          </div>
          <h4 className={`text-2xl font-bold ${result.passed ? 'text-green-400' : 'text-red-400'}`}>
            {result.passed ? 'Quiz Passed!' : `You need ${passingScore}% to move on`}
          </h4>
          <p className="text-4xl font-bold text-foreground my-4" data-testid="quiz-score">
            {Math.round(result.score)}%
          </p>
          <p className="text-muted-foreground mb-6">
            {result.correct} of {result.total} correct
          </p>
          <div className="flex items-center justify-center gap-3">
            {!result.passed && (
              <Button onClick={retry} className="bg-brand-gold text-black hover:brightness-110" data-testid="quiz-retry-btn">
                <RotateCw className="w-4 h-4 mr-2" /> Retry Quiz
              </Button>
            )}
            {result.passed && (
              <Button onClick={onClose} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="quiz-continue-btn">
                Continue →
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {shuffled.map((q, qIndex) => {
            const qType = q.type || 'mcq';
            return (
              <div key={qIndex} className="bg-slate-900/50 p-4 rounded-lg" data-testid={`quiz-question-${qIndex}`}>
                <p className="font-medium text-foreground mb-3">
                  <span className="text-xs uppercase tracking-wider text-brand-gold mr-2">
                    {qType === 'mcq' ? 'Multiple choice' : qType === 'tf' ? 'True / False' : 'Short answer'}
                  </span>
                  <br />
                  {qIndex + 1}. {q.question}
                </p>

                {(qType === 'mcq' || qType === 'tf') && Array.isArray(q.options) && (
                  <div className="space-y-2">
                    {q.options.map((opt, optIndex) => (
                      <label
                        key={optIndex}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition ${
                          answers[qIndex] === optIndex
                            ? 'bg-primary/20 border border-primary'
                            : 'bg-slate-800/50 hover:bg-slate-800'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${qIndex}`}
                          checked={answers[qIndex] === optIndex}
                          onChange={() => setAnswer(qIndex, optIndex)}
                          className="sr-only"
                          data-testid={`quiz-option-${qIndex}-${optIndex}`}
                        />
                        <span className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                          answers[qIndex] === optIndex ? 'border-primary bg-primary' : 'border-muted-foreground'
                        }`}>
                          {answers[qIndex] === optIndex && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className="text-foreground">{opt}</span>
                      </label>
                    ))}
                  </div>
                )}

                {qType === 'short' && (
                  <input
                    type="text"
                    value={answers[qIndex] ?? ''}
                    onChange={(e) => setAnswer(qIndex, e.target.value)}
                    placeholder="Type your answer…"
                    className="w-full bg-slate-800/50 border border-white/10 rounded-lg px-3 py-2 text-foreground focus:border-brand-gold outline-none"
                    data-testid={`quiz-short-input-${qIndex}`}
                  />
                )}
              </div>
            );
          })}
          <Button
            onClick={submit}
            disabled={submitting || !allAnswered}
            className="w-full bg-primary text-primary-foreground py-6 disabled:opacity-50"
            data-testid="quiz-submit-btn"
          >
            {submitting ? 'Grading…' : allAnswered ? 'Submit Quiz' : 'Answer all questions to submit'}
          </Button>
        </div>
      )}
    </div>
  );
};
